import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ShipmentDetails from '../components/ShipmentDetails';
import './DeliveryNoteDetails.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function DeliveryNoteDetails({ token }) {
  const { deliveryId } = useParams();
  const navigate = useNavigate();

  const [deliveryNote, setDeliveryNote] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [packing_list, setPackingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDepartureModal, setShowDepartureModal] = useState(false);
  const [departureDateTime, setDepartureDateTime] = useState('');
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [arrivalDateTime, setArrivalDateTime] = useState('');
  const [documentViewer, setDocumentViewer] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (documentViewer?.url) {
        window.URL.revokeObjectURL(documentViewer.url);
      }
    };
  }, [documentViewer]);

  useEffect(() => {
    if (token) {
      fetchDeliveryNote();
    }
  }, [deliveryId, token]);

  const closeDocumentViewer = () => {
    setDocumentViewer((current) => {
      if (current?.url) {
        window.URL.revokeObjectURL(current.url);
      }
      return null;
    });
  };

  const handleDocument = async (docType, action) => {
    try {
      setError('');
      setDocumentLoading(true);

      const endpoint = docType === 'signed'
        ? `${API_URL}/delivery-notes/${deliveryId}/download-signed`
        : `${API_URL}/delivery-notes/${deliveryId}/pdf`;
      const disposition = action === 'view' ? 'view' : 'download';

      const response = await fetch(`${endpoint}?disposition=${disposition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load document');
      }

      const blob = await response.blob();
      const contentType = response.headers.get('content-type') || blob.type;

      if (action === 'view') {
        closeDocumentViewer();
        const url = window.URL.createObjectURL(blob);
        const title = docType === 'signed'
          ? `Signed Copy — ${deliveryNote.delivery_number}`
          : `Delivery Note — ${deliveryNote.delivery_number}`;
        const type = contentType.includes('pdf') ? 'pdf' : 'image';
        setDocumentViewer({ url, title, type });
        return;
      }

      const ext = contentType.includes('pdf') ? 'pdf' : 'jpg';
      const filename = docType === 'signed'
        ? `signed-${deliveryNote.delivery_number}.${ext}`
        : `delivery-note-${deliveryNote.delivery_number}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDocumentLoading(false);
    }
  };

  const fetchDeliveryNote = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Delivery note not found');
      }

      const data = await response.json();
      setDeliveryNote(data);
      setEditForm({
        delivery_date: data.delivery_date || '',
        customer_name: data.customer_name || '',
        delivery_address: data.delivery_address || '',
        delivery_city: data.delivery_city || '',
        delivery_country: data.delivery_country || '',
        truck_model: data.truck_model || '',
        truck_registration: data.truck_registration || '',
        driver_name: data.driver_name || '',
        driver_phone: data.driver_phone || '',
        notes: data.notes || ''
      });

      // Fetch shipment details
      if (data.shipment_id) {
        const shipmentRes = await fetch(`${API_URL}/shipments/${data.shipment_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (shipmentRes.ok) {
          const shipmentData = await shipmentRes.json();
          setShipment(shipmentData);

          // Fetch packing list
          if (shipmentData.packing_list_id) {
            const packingRes = await fetch(`${API_URL}/packing-lists/${shipmentData.packing_list_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (packingRes.ok) {
              const packingData = await packingRes.json();
              setPackingList(packingData);
            }
          }
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update delivery note');
      }

      setSuccess('Delivery note updated successfully');
      setIsEditing(false);
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      delivery_date: deliveryNote.delivery_date || '',
      customer_name: deliveryNote.customer_name || '',
      delivery_address: deliveryNote.delivery_address || '',
      delivery_city: deliveryNote.delivery_city || '',
      delivery_country: deliveryNote.delivery_country || '',
      truck_model: deliveryNote.truck_model || '',
      truck_registration: deliveryNote.truck_registration || '',
      driver_name: deliveryNote.driver_name || '',
      driver_phone: deliveryNote.driver_phone || '',
      notes: deliveryNote.notes || ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this delivery note?')) return;

    try {
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete delivery note');
      }

      setSuccess('Delivery note deleted');
      setTimeout(() => navigate('/delivery-notes'), 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMarkReady = async () => {
    try {
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'ready' })
      });

      if (!response.ok) throw new Error('Failed to update status');

      setSuccess('Marked as ready');
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadPDF = () => handleDocument('original', 'download');
  const handleViewPDF = () => handleDocument('original', 'view');
  const handleDownloadSigned = () => handleDocument('signed', 'download');
  const handleViewSigned = () => handleDocument('signed', 'view');

  const handleDeparture = async () => {
    console.log('handleDeparture called');
    console.log('departureDateTime:', departureDateTime);
    
    if (!departureDateTime) {
      console.log('No departure date/time entered');
      setError('Please enter departure date and time');
      return;
    }

    try {
      console.log('Sending departure request...');
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'in_transit',
          departure_datetime: new Date(departureDateTime).toISOString()
        })
      });

      console.log('Response status:', response.status);

      if (!response.ok) throw new Error('Failed to record departure');

      console.log('Departure recorded successfully');
      setSuccess('Departure recorded');
      setShowDepartureModal(false);
      setDepartureDateTime('');
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      console.error('Departure error:', err);
      setError(err.message);
    }
  };

  const handleArrival = async () => {
    if (!arrivalDateTime) {
      setError('Please enter arrival date and time');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/arrival`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          arrival_datetime: new Date(arrivalDateTime).toISOString()
        })
      });

      if (!response.ok) throw new Error('Failed to record arrival');

      setSuccess('Arrival recorded');
      setShowArrivalModal(false);
      setArrivalDateTime('');
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUploadSigned = async () => {
    if (!uploadFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/upload-signed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to upload signed document');

      setSuccess('Signed document uploaded. Status updated to Delivered');
      setShowUploadForm(false);
      setUploadFile(null);
      setUploading(false);
      setTimeout(() => fetchDeliveryNote(), 1500);
    } catch (err) {
      setError(err.message);
      setUploading(false);
    }
  };

  if (loading) return <div className="loading">Loading delivery note...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!deliveryNote) return <div className="alert alert-danger">Delivery note not found</div>;

  const getStatusColor = (status) => {
    const colors = { draft: 'warning', ready: 'info', in_transit: 'primary', delivered: 'success' };
    return colors[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const labels = { draft: 'Draft', ready: 'Ready', in_transit: 'In Transit', delivered: 'Delivered' };
    return labels[status] || status;
  };

  return (
    <div className="delivery-note-details-container">
      <div className="details-header">
        <h1>Delivery Note {deliveryNote.delivery_number}</h1>
        <span className={`status-badge badge-${getStatusColor(deliveryNote.status)}`}>
          {getStatusLabel(deliveryNote.status)}
        </span>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Shipment Details Block */}
      <ShipmentDetails 
        shipment={shipment} 
        packing_list={packing_list}
        loading={!shipment}
      />

      {/* Timeline */}
      <div className="delivery-timeline">
        <div className={`timeline-step ${deliveryNote.status !== 'draft' || deliveryNote.status === 'draft' ? 'completed' : ''}`}>
          <div className="step-marker">✓</div>
          <div className="step-label">Created</div>
        </div>
        <div className={`timeline-step ${['ready', 'in_transit', 'delivered'].includes(deliveryNote.status) ? 'completed' : ''}`}>
          <div className="step-marker">✓</div>
          <div className="step-label">Ready</div>
        </div>
        <div className={`timeline-step ${['in_transit', 'delivered'].includes(deliveryNote.status) ? 'completed' : ''}`}>
          <div className="step-marker">🚚</div>
          <div className="step-label">In Transit</div>
        </div>
        <div className={`timeline-step ${deliveryNote.status === 'delivered' ? 'completed' : ''}`}>
          <div className="step-marker">✓</div>
          <div className="step-label">Delivered</div>
        </div>
      </div>

      {/* Delivery Details */}
      <div className="section">
        <h3>Delivery Information</h3>
        {!isEditing ? (
          <div className="info-display">
            <div className="info-row">
              <span className="label">Customer Name:</span>
              <span className="value">{deliveryNote.customer_name}</span>
            </div>
            <div className="info-row">
              <span className="label">Delivery Address:</span>
              <span className="value">{deliveryNote.delivery_address}</span>
            </div>
            <div className="info-row">
              <span className="label">City:</span>
              <span className="value">{deliveryNote.delivery_city}</span>
            </div>
            <div className="info-row">
              <span className="label">Country:</span>
              <span className="value">{deliveryNote.delivery_country}</span>
            </div>
            <div className="info-row">
              <span className="label">Truck Model:</span>
              <span className="value">{deliveryNote.truck_model}</span>
            </div>
            <div className="info-row">
              <span className="label">Truck Registration:</span>
              <span className="value">{deliveryNote.truck_registration}</span>
            </div>
            <div className="info-row">
              <span className="label">Driver Name:</span>
              <span className="value">{deliveryNote.driver_name}</span>
            </div>
            <div className="info-row">
              <span className="label">Driver Phone:</span>
              <span className="value">{deliveryNote.driver_phone}</span>
            </div>
            <div className="info-row">
              <span className="label">Notes:</span>
              <span className="value">{deliveryNote.notes || 'None'}</span>
            </div>
            {deliveryNote.departure_datetime && (
              <div className="info-row">
                <span className="label">Departure:</span>
                <span className="value">{new Date(deliveryNote.departure_datetime).toLocaleString()}</span>
              </div>
            )}
            {deliveryNote.arrival_datetime && (
              <div className="info-row">
                <span className="label">Arrival:</span>
                <span className="value">{new Date(deliveryNote.arrival_datetime).toLocaleString()}</span>
              </div>
            )}
          </div>
        ) : (
          <form className="edit-form">
            <input type="date" name="delivery_date" value={editForm.delivery_date} onChange={handleInputChange} placeholder="Delivery Date" />
            <input type="text" name="customer_name" value={editForm.customer_name} onChange={handleInputChange} placeholder="Customer Name" />
            <textarea name="delivery_address" value={editForm.delivery_address} onChange={handleInputChange} placeholder="Delivery Address" rows="3" />
            <input type="text" name="delivery_city" value={editForm.delivery_city} onChange={handleInputChange} placeholder="City" />
            <input type="text" name="delivery_country" value={editForm.delivery_country} onChange={handleInputChange} placeholder="Country" />
            <input type="text" name="truck_model" value={editForm.truck_model} onChange={handleInputChange} placeholder="Truck Model" />
            <input type="text" name="truck_registration" value={editForm.truck_registration} onChange={handleInputChange} placeholder="Truck Registration" />
            <input type="text" name="driver_name" value={editForm.driver_name} onChange={handleInputChange} placeholder="Driver Name" />
            <input type="text" name="driver_phone" value={editForm.driver_phone} onChange={handleInputChange} placeholder="Driver Phone" />
            <textarea name="notes" value={editForm.notes} onChange={handleInputChange} placeholder="Notes" rows="3" />
          </form>
        )}
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        {deliveryNote.status === 'draft' && (
          <>
            <button className="btn btn-secondary" onClick={handleEdit}>Edit</button>
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            <button className="btn btn-primary" onClick={handleMarkReady}>Mark Ready</button>
            {isEditing && (
              <>
                <button className="btn btn-success" onClick={handleSave}>Save</button>
                <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
              </>
            )}
          </>
        )}

        {deliveryNote.status === 'ready' && (
          <>
            {!isEditing ? (
              <>
                <button className="btn btn-secondary" onClick={handleEdit}>✏️ Edit</button>
                <button className="btn btn-danger" onClick={handleDelete}>🗑️ Delete</button>
                <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
                  👁 View PDF
                </button>
                <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
                  📥 Download PDF
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={() => {
                    console.log('Truck Departing button clicked');
                    setShowDepartureModal(true);
                  }}
                >
                  🚚 Truck Departing
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-success" onClick={handleSave}>💾 Save</button>
                <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
              </>
            )}
          </>
        )}

        {deliveryNote.status === 'in_transit' && (
          <>
            <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
              👁 View PDF
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
              📥 Download PDF
            </button>
            <button className="btn btn-success" onClick={() => setShowArrivalModal(true)}>✓ Arrived at Customer</button>
            {deliveryNote.arrival_datetime && (
              <button className="btn btn-info" onClick={() => setShowUploadForm(true)}>📤 Upload Signed Copy</button>
            )}
          </>
        )}

        {deliveryNote.status === 'delivered' && (
          <>
            {deliveryNote.signed_document_path ? (
              <>
                <button className="btn btn-secondary" onClick={handleViewSigned} disabled={documentLoading}>
                  👁 View Signed Copy
                </button>
                <button className="btn btn-success" onClick={handleDownloadSigned} disabled={documentLoading}>
                  📄 Download Signed Copy
                </button>
                <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
                  👁 View Original PDF
                </button>
                <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
                  📥 Download Original PDF
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
                  👁 View PDF
                </button>
                <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
                  📥 Download PDF
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showDepartureModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowDepartureModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{margin: '0 0 20px 0', color: '#1a3a52'}}>Record Truck Departure</h3>
            <p style={{color: '#666', fontSize: '13px', marginBottom: '15px'}}>
              Enter the date and time when the truck departs
            </p>
            <input
              type="datetime-local"
              value={departureDateTime}
              onChange={(e) => setDepartureDateTime(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => setShowDepartureModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeparture}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Record Departure
              </button>
            </div>
          </div>
        </div>
      )}

      {showArrivalModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowArrivalModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{margin: '0 0 20px 0', color: '#1a3a52'}}>Record Truck Arrival</h3>
            <p style={{color: '#666', fontSize: '13px', marginBottom: '15px'}}>
              Enter the date and time when the truck arrives at the customer
            </p>
            <input
              type="datetime-local"
              value={arrivalDateTime}
              onChange={(e) => setArrivalDateTime(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => setShowArrivalModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleArrival}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Record Arrival
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadForm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{margin: '0 0 20px 0', color: '#1a3a52'}}>Upload Signed Delivery Note</h3>
            <p style={{color: '#666', fontSize: '13px', marginBottom: '15px'}}>
              Upload the signed delivery note (PDF or JPG)
            </p>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg"
              onChange={(e) => setUploadFile(e.target.files[0])}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            {uploadFile && (
              <p style={{fontSize: '12px', color: '#666', marginBottom: '15px'}}>
                ✓ Selected: {uploadFile.name}
              </p>
            )}
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleUploadSigned}
                disabled={uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploading ? '#ccc' : '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {documentViewer && (
        <div className="document-viewer-overlay" onClick={closeDocumentViewer}>
          <div className="document-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="document-viewer-header">
              <h3>{documentViewer.title}</h3>
              <button type="button" className="document-viewer-close" onClick={closeDocumentViewer}>
                Close
              </button>
            </div>
            <div className="document-viewer-body">
              {documentViewer.type === 'pdf' ? (
                <iframe
                  src={documentViewer.url}
                  title={documentViewer.title}
                  className="document-viewer-frame"
                />
              ) : (
                <img
                  src={documentViewer.url}
                  alt={documentViewer.title}
                  className="document-viewer-image"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
